import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GroupRegisterRequest } from '@app/core/models/auth/group-register-request';
import { ErrorResponse } from '@app/core/models/commom/error-response';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { Checkbox } from '@interfaces/a-organizar/checkbox';
import { Claim } from '@interfaces/a-organizar/claim';
import { Permission } from '@interfaces/a-organizar/permission';
import { PermissionGroup } from '@interfaces/a-organizar/permissionGroup';
import { Option } from '@interfaces/common/option';
import { TranslateService } from '@ngx-translate/core';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { UserService } from '@services/http/user.service';
import { nameof } from '@src/app/core/utils/nameof.utils';
import { MenuItem } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { Permissions } from 'src/app/core/guard/Permissions';

@Component({
  templateUrl: './group-register.component.html',
  styleUrls: ['./group-register.component.scss'],
  standalone: false,
})
export class GroupRegisterComponent extends VaultBaseComponent implements OnInit {
  groupId: string = '';
  defaultLanguage: string = 'pt-BR';
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  public groupRegisterProps = nameof<GroupRegisterRequest>();
  groupsRegisterForm = this.formBuilder.group({
    [this.groupRegisterProps.search]: [''],
    [this.groupRegisterProps.name]: ['', [Validators.required]],
    [this.groupRegisterProps.roleId]: ['', [Validators.required]],
    [this.groupRegisterProps.permissions]: this.formBuilder.array([], [Validators.required]),
  });
  groupPermissions = Permissions.PermissionGroup;
  userTypeOptions: Option[] = [];
  selectedUserType: Option | null = null;
  permissionsGroups: PermissionGroup[] = [];
  permissions: Permission[] = [];
  permissionsDescriptions: string[] = [];
  permissionsToSend: string[] = [];
  checkAllPermissions: Checkbox[] = [
    {
      label: this.t(this.tk.User.MarkAll),
      name: 'mark-all',
      value: 'mark-all',
      isDisabled: false,
      isChecked: false,
    },
  ];
  _permissionsCheckboxes: Checkbox[] | null = null;

  constructor(
    translateService: TranslateService,
    private utilsService: UtilsService,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private formErrorHandler: FormErrorHandlerService,
    private toast: ToastHelperService,
    private cdr: ChangeDetectorRef
  ) {
    super(translateService);
    this.defaultLanguage = translateService.currentLang;
  }

  ngOnInit(): void {
    this.breadcrumbs = [
      { label: this.t(this.tk.General.Administration), routerLink: '/' },
      {
        label: this.t(this.tk.General.Groups),
        routerLink: '/dashboard/groups/list',
      },
      {
        label: this.t(this.tk.General.Register),
        routerLink: '/dashboard/groups/register',
      },
    ];

    this.getUserTypesOptions();
    this.getVerifyGroupId();
  }

  getVerifyGroupId(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.groupId = params['id'];
        this.utilsService.setIsLoading(true);
        this.userService.getPermissionGroupById(this.groupId).subscribe({
          next: response => {
            this.utilsService.setIsLoading(false);
            if (response) {
              const permissionGroup = response;

              this.groupsRegisterForm
                .get(this.groupRegisterProps.name)
                ?.setValue(permissionGroup.name);

              this.groupsRegisterForm
                .get(this.groupRegisterProps.roleId)
                ?.setValue(permissionGroup.roleId);

              this.selectedUserType =
                this.userTypeOptions.find(userType => userType.value === permissionGroup.roleId) ||
                null;

              // Invalida o cache antes de definir permissionsToSend para garantir regeneração correta
              this._permissionsCheckboxes = null;
              this.permissionsToSend = [...permissionGroup.permissions];

              this.getUserPermissionsAll(permissionGroup.roleId);
            }
          },
          error: error => {
            this.utilsService.setIsLoading(false);
            console.log('getPermissionGroupById error: ', error);

            this.toast.showToastError(
              this.t(this.tk.User.UserErrorLoadingGroups),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
      }
    });
  }

  handleOptionChange(selectedOptions: Option | null, key: string): void {
    switch (key) {
      case 'selectedUserType':
        this.selectedUserType = selectedOptions;

        this.groupsRegisterForm
          .get(this.groupRegisterProps.roleId)
          ?.setValue(selectedOptions?.value || '');

        // Invalida o cache quando o tipo de usuário muda
        this._permissionsCheckboxes = null;

        // Só limpa permissionsToSend se não estiver editando um grupo existente
        // Quando editando, permissionsToSend já foi definido em getVerifyGroupId
        // e não deve ser limpo quando o select dispara o evento durante o carregamento
        if (!this.groupId || this.groupId.length === 0) {
          this.permissionsToSend = [];
        }

        if (selectedOptions?.value) {
          this.getUserPermissionsAll(selectedOptions?.value);
        }
        break;
    }
  }

  getTabHeader(permission: Permission): string {
    // Conta quantas claims desta permissão estão selecionadas
    const selectedCount = permission.claims.filter(claim =>
      this.permissionsToSend.includes(claim.value)
    ).length;

    // Adiciona 1 para a permissão principal se ela estiver selecionada
    const mainPermissionSelected = this.permissionsToSend.includes(permission.description) ? 1 : 0;

    // Total de claims possíveis mais a permissão principal (já descartando o "Todos")
    const totalPossible = permission.claims.length;

    // Total selecionado (claims + permissão principal)
    const totalSelected = selectedCount + mainPermissionSelected;

    return `(${totalSelected}/${totalPossible})`;
  }

  async getUserPermissionsAll(roleId: string): Promise<void> {
    this.utilsService.setIsLoading(true);
    try {
      const response = await firstValueFrom(this.userService.getAllUsersPermissions(roleId));
      this.utilsService.setIsLoading(false);

      if (response) {
        this.permissions = response;
        this.resetPermissionGroupFormArray();
        this.permissionsDescriptions = [];

        this.permissions.forEach((permission: Permission) => {
          const control = this.formBuilder.control('');
          this.permissionGroupFormArray.push(control);
          this.permissionsDescriptions.push(permission.name);
        });

        // Invalida o cache para forçar regeneração dos checkboxes com os valores corretos
        this._permissionsCheckboxes = null;

        // Força detecção de mudanças para garantir que o template recalcule os getters
        this.cdr.detectChanges();
      }
    } catch (error) {
      if (error instanceof ErrorResponse) {
        this.utilsService.setIsLoading(false);
        console.log('getAllUsersPermissions error: ', error);

        this.formErrorHandler.showToast(error, this.tk.User.UserErrorLoadingPermissions);
      }
    }
  }

  resetPermissionGroupFormArray() {
    this.permissionGroupFormArray.clear();
  }

  get permissionGroupFormArray(): FormArray {
    return this.groupsRegisterForm.get(this.groupRegisterProps.permissions) as FormArray;
  }

  getUserTypesOptions(): void {
    this.utilsService.setIsLoading(true);
    this.userService.getUserRoles().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.userTypeOptions = response.map(
            role =>
              ({
                label: role.name,
                value: role.id,
              }) as Option
          );

          // Se já existe um grupoId e selectedUserType ainda não foi definido, atualiza agora
          if (this.groupId && this.groupsRegisterForm.get(this.groupRegisterProps.roleId)?.value) {
            const roleId = this.groupsRegisterForm.get(this.groupRegisterProps.roleId)?.value;
            this.selectedUserType =
              this.userTypeOptions.find(userType => userType.value === roleId) || null;
          }
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.log('getUserRoles error: ', error);

        this.toast.showToastError(
          this.t(this.tk.User.UserErrorLoadingRoles),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  get permissionsCheckboxes(): Checkbox[] {
    if (!this._permissionsCheckboxes) {
      this._permissionsCheckboxes = this.generateCheckboxes(this.permissions);
      return this._permissionsCheckboxes;
    }
    return this._permissionsCheckboxes!;
  }

  get checkboxesFormClaims(): Checkbox[][] {
    const claimsCheckbox = this.permissions.map(permission => {
      return permission.claims.map((claim, i) => {
        return this.generateCheckboxesFormClaims(permission.claims, permission.name, i);
      });
    });
    return claimsCheckbox;
  }

  generateCheckboxes(permissions: Permission[] = []): Checkbox[] {
    return permissions.flatMap(permission => {
      const permissionChecked = this.permissionsToSend.includes(permission.description);
      const permissionCheckbox = this.createCheckbox(
        this.t(this.tk.General.All),
        permission.description,
        permission.description,
        permissionChecked
      );

      return permissionCheckbox;
    });
  }

  generateCheckboxesFormClaims(claim: Claim[] = [], ref: string, index: number): Checkbox {
    const checkboxes = claim.map(claimItem => {
      const claimChecked = this.permissionsToSend.includes(claimItem.value);
      const claimCheckbox = this.createCheckbox(
        claimItem.description,
        claimItem.name,
        claimItem.value,
        claimChecked,
        false,
        ref
      );

      return claimCheckbox;
    });

    return checkboxes[index];
  }

  createCheckbox(
    label: string = '',
    name: string = '',
    value: string = '',
    isChecked: boolean = false,
    isDisabled: boolean = false,
    ref?: string
  ): Checkbox {
    const checkbox: Checkbox = {
      label,
      name,
      value,
      isChecked,
      isDisabled,
    };

    if (ref) checkbox.ref = ref;

    return checkbox;
  }

  verifyPermissionsContainsDescription(description: string): boolean {
    return this.permissionsToSend.includes(description);
  }

  handleCheckedChange(checkbox: Checkbox, level: 'main' | 'sub'): void {
    if (level === 'main') {
      const permission = this.permissions.find(
        permission => permission.description === checkbox.name
      );

      if (checkbox.isChecked) {
        if (!this.verifyPermissionsContainsDescription(permission?.description || '')) {
          this.permissionsToSend.push(permission?.description || '');
        }
        permission?.claims.forEach(claim => {
          if (!this.verifyPermissionsContainsDescription(claim.value)) {
            this.permissionsToSend.push(claim.value);
          }
        });
      } else {
        this.permissionsToSend = this.permissionsToSend.filter(
          description => description !== permission?.description
        );
        permission?.claims.forEach(claim => {
          this.permissionsToSend = this.permissionsToSend.filter(
            description => description !== claim.value
          );
        });
      }
    }

    if (level === 'sub') {
      if (checkbox.isChecked) {
        if (!this.verifyPermissionsContainsDescription(checkbox.value)) {
          this.permissionsToSend.push(checkbox.value);
        }
      } else {
        this.permissionsToSend = this.permissionsToSend.filter(
          description => description !== checkbox.value
        );
      }
    }
  }

  handleCheckAllPermissions(checkboxes: Checkbox[]): void {
    const isChecked = checkboxes[0].isChecked;
    this.checkAllPermissions[0].isChecked = isChecked;

    this.permissions.forEach((permission, index) => {
      this.permissionGroupFormArray.controls[index].setValue(
        isChecked ? permission.description : ''
      );
    });
  }

  get permissionsCountSelected(): number {
    return this.permissionsToSend.length;
  }

  cancelRegister(): void {
    this.router.navigate(['dashboard/groups/list']);
  }

  registerGroup(): void {
    if (this.groupsRegisterForm.invalid) {
      this.groupsRegisterForm.markAllAsTouched();
      return;
    }

    const formValue = this.groupsRegisterForm.getRawValue() as unknown as GroupRegisterRequest;

    const groupRegisterRequest = {
      name: formValue.name,
      roleId: formValue.roleId,
      permissions: this.permissionsToSend,
    } as GroupRegisterRequest;

    if (this.groupId) {
      this.editGroup(groupRegisterRequest);
    } else {
      this.postGroup(groupRegisterRequest);
    }
  }

  postGroup(groupData: GroupRegisterRequest): void {
    this.utilsService.setIsLoading(true);
    this.userService.postPermissionGroup(groupData).subscribe({
      next: () => {
        this.utilsService.setIsLoading(false);
        this.toast.showToastSuccess(this.t(this.tk.User.GroupCreatedSuccessfully));
        this.router.navigate(['dashboard/groups/list']);
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        if (error instanceof ErrorResponse) {
          this.formErrorHandler.applyFormErrors(this.groupsRegisterForm, error);
        } else {
          console.log('postPermissionGroup error: ', error);
        }

        this.toast.showToastError(
          this.t(this.tk.User.UserErrorCreatingPermissionsGroup),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  editGroup(groupData: GroupRegisterRequest): void {
    this.utilsService.setIsLoading(true);
    this.userService.putPermissionGroup(groupData, this.groupId).subscribe({
      next: () => {
        this.utilsService.setIsLoading(false);
        this.toast.showToastSuccess(this.t(this.tk.User.GroupUpdatedSuccessfully));
        this.router.navigate(['dashboard/groups/list']);
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        if (error instanceof ErrorResponse) {
          this.formErrorHandler.applyFormErrors(this.groupsRegisterForm, error);
        } else {
          console.log('putPermissionGroup error: ', error);
        }
        this.toast.showToastError(
          this.t(this.tk.User.UserErrorUpdatingPermissionsGroup),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }
}
